import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NiIconComponent } from '../ni-icon/ni-icon.component';

export interface NiOption {
  value: any;
  label: string;
  code?: string;
  disabled?: boolean;
  group?: string;
  icon?: string;
  trailingIcon?: string;
  profileImage?: string;
  trailingProfileImage?: string;
  imageLoaded?: boolean;
  trailingImageLoaded?: boolean;
  isFavorite?: boolean;
  badgeCount?: number;
  [key: string]: any;
}

@Component({
  selector: 'NiFormOption',
  templateUrl: './ni-option.component.html',
  styleUrls: ['./ni-option.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, NiIconComponent],
})
export class NiOptionComponent {
  constructor(private readonly  cdr: ChangeDetectorRef) {}
  @Input() option!: NiOption;
  @Input() multiple: boolean = false;
  @Input() selected: boolean = false;
  @Input() highlighted: boolean = false;
  @Input() optionLabel: string | number = 'label';
  @Input() checkIcon: string = 'check';
  @Input() showFavoriteToggle: boolean = true;
  @Input() showBadge: boolean = true;
  /** When true, styles trailing " - Xmiles" in Figma success green (#2E7D32). */
  @Input() highlightDistanceMiles: boolean = false;

  @Output() optionClick = new EventEmitter<NiOption>();
  @Output() imageError = new EventEmitter<{
    event: Event;
    option: NiOption;
    type: 'profileImage' | 'trailingProfileImage';
  }>();
  @Output() favoriteToggle = new EventEmitter<{ option: NiOption; isFavorite: boolean }>();

  getOptionLabel(option: NiOption): string {
    return option[this.optionLabel] || option['label'] || option['name'] || option['value'] || '';
  }

  /** Figma success green used for distance miles suffix. */
  readonly distanceMilesColor = '#2E7D32';

  private readonly distanceMilesPattern =
    /^(.*?)(\s*-\s*\d+(?:\.\d+)?\s*miles?(?:\s*\(Recommended\))?)\s*$/i;

  splitDistanceLabel(label: string): { name: string; miles: string } | null {
    if (!this.highlightDistanceMiles || !label) {
      return null;
    }
    const match = String(label).match(this.distanceMilesPattern);
    if (!match) {
      return null;
    }
    return { name: match[1], miles: match[2] };
  }

  getFirstLetter(option: NiOption): string {
    const label = this.getOptionLabel(option);
    return label.charAt(0).toUpperCase();
  }

  isOptionSelected(option: NiOption): boolean {
    return this.selected;
  }

  onImageError(
    event: Event,
    option: NiOption,
    type: 'profileImage' | 'trailingProfileImage'
  ): void {
    // Mark the image as not loaded based on type
    if (type === 'profileImage') {
      option['imageLoaded'] = false;
    } else if (type === 'trailingProfileImage') {
      option['trailingImageLoaded'] = false;
    }

    // Emit the event to parent component
    this.imageError.emit({ event, option, type });

    // Trigger change detection to update the UI
    this.cdr.detectChanges();
  }

  onOptionMouseDown(event: MouseEvent): void {
    // Prevent blur from closing dropdown before click event fires
    // But don't prevent default to allow normal click behavior
    event.stopPropagation();
  }

  onOptionClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    // Prevent click if option doesn't exist or is disabled
    if (!this.option || this.option.disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Stop propagation to prevent parent click handlers from interfering
    // But allow the click to proceed normally
    event.stopPropagation();
    
    // Emit the option immediately
    this.optionClick.emit(this.option);

    // Trigger change detection for OnPush strategy
    this.cdr.markForCheck();
  }

  onFavoriteToggle(event: Event): void {
    // Stop propagation to prevent option click from firing when toggling favorite
    event.stopPropagation();
    event.preventDefault();
    const newFavoriteState = !this.option.isFavorite;
    this.favoriteToggle.emit({ option: this.option, isFavorite: newFavoriteState });
  }

  private touchStartY = 0;
  private touchMoved = false;
  onTouchStart(event: TouchEvent) {
    this.touchStartY = event.touches[0].clientY;
    this.touchMoved = false;
  }

  onTouchMove(event: TouchEvent) {
    const deltaY = Math.abs(event.touches[0].clientY - this.touchStartY);
    if (deltaY > 10) {   // threshold to consider scroll
      this.touchMoved = true;
    }
  }

  onTouchEnd(event: TouchEvent) {
    if (!this.touchMoved) {
      // treat as normal click only if NOT scrolling
      this.onOptionClick(event);
    }
  }

}
